// map-animation.service.ts
import { EventEmitter, inject, Injectable } from '@angular/core';
import { GeoJSONSource, Map as MaplibreMap } from 'maplibre-gl';
import { AnimationStateService } from './animation-state.service';
import { MapStateService } from './map-state.service';
import { Position } from '@wheere/api-tracker';

export interface PositionWithColor extends Position {
  color: string;
}

@Injectable({
  providedIn: 'root',
})
export class MapAnimationPointService {
  private mapStateService = inject(MapStateService);
  private animationStateService = inject(AnimationStateService);

  public animationCompleted = new EventEmitter<void>();

  private positions: PositionWithColor[] = [];
  private pointSourceId = 'animated-point-source';
  private pointLayerId = 'animated-point-layer';
  private animationInterval = 100;

  public startAnimation(positions: PositionWithColor[], interval = 100) {
    this.animationInterval = interval;
    const currentAnimationState = this.animationStateService.animationState();
    const map = this.mapStateService.map();
    if (!map) return;

    if (currentAnimationState === 'paused') {
      this.animationStateService.animationState.set('playing');
      this.animatePoints();
    } else {
      this.animationStateService.currentCounter = 0;
      this.positions = positions;
      this.animationStateService.animationState.set('playing');

      if (map.getLayer(this.pointLayerId)) {
        map.removeLayer(this.pointLayerId);
      }
      if (map.getSource(this.pointSourceId)) {
        map.removeSource(this.pointSourceId);
      }

      const firstPosition = positions[0];
      const initialPoint: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'Point',
              coordinates: [firstPosition.longitude!, firstPosition.latitude!],
            },
          },
        ],
      };

      map.addSource(this.pointSourceId, {
        type: 'geojson',
        data: initialPoint,
      });

      map.addLayer({
        id: this.pointLayerId,
        source: this.pointSourceId,
        type: 'circle',
        paint: {
          'circle-radius': 6,
          'circle-color': firstPosition.color,
        },
      });

      this.animatePoints();
    }
  }

  public pauseAnimation() {
    if (this.animationStateService.animationState() === 'playing') {
      this.animationStateService.animationState.set('paused');
    }
  }

  public stopAnimation() {
    this.animationStateService.animationState.set('stopped');

    const map = this.mapStateService.map();
    if (map) {
      try {
        if (map.getLayer(this.pointLayerId)) {
          map.removeLayer(this.pointLayerId);
        }
        if (map.getSource(this.pointSourceId)) {
          map.removeSource(this.pointSourceId);
        }
      } catch (e) {
        // Ignoré.
      }
    }

    this.animationStateService.currentCounter = 0;
    this.positions = [];

    setTimeout(() => {
      this.animationCompleted.emit();
    }, 50);
  }

  private animatePoints() {
    const map = this.mapStateService.map();
    if (!map) return;
    const totalPoints = this.positions.length;

    const animate = () => {
      const state = this.animationStateService.animationState();
      if (state !== 'playing') {
        return;
      }

      const index = this.animationStateService.currentCounter;
      if (index < totalPoints) {
        const pos = this.positions[index];
        const newCoord: [number, number] = [pos.longitude!, pos.latitude!];

        const source = map.getSource(this.pointSourceId) as GeoJSONSource;
        if (!source) return;
        const data = source._data as unknown as GeoJSON.FeatureCollection;
        if ('coordinates' in data.features[0].geometry) {
          data.features[0].geometry.coordinates = newCoord;
        }
        source.setData(data);

        map.setPaintProperty(this.pointLayerId, 'circle-color', pos.color);

        this.animationStateService.currentCounter++;
        setTimeout(() => {
          requestAnimationFrame(animate);
        }, this.animationInterval);
      } else {
        try {
          if (map.getLayer(this.pointLayerId)) {
            map.removeLayer(this.pointLayerId);
          }
          if (map.getSource(this.pointSourceId)) {
            map.removeSource(this.pointSourceId);
          }
        } catch (e) {
          // Ignoré.
        }

        this.animationStateService.animationState.set('stopped');

        this.animationStateService.currentCounter = 0;

        setTimeout(() => {
          this.animationCompleted.emit();
        }, 50);
      }
    };

    animate();
  }

  public recreateAnimationPoint(map: MaplibreMap, index: number) {
    if (this.positions.length === 0 || index >= this.positions.length) return;

    try {
      const pos = this.positions[index];

      const pointData: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'Point',
              coordinates: [pos.longitude!, pos.latitude!],
            },
          },
        ],
      };

      if (!map.getSource(this.pointSourceId)) {
        map.addSource(this.pointSourceId, {
          type: 'geojson',
          data: pointData,
        });

        map.addLayer({
          id: this.pointLayerId,
          source: this.pointSourceId,
          type: 'circle',
          paint: {
            'circle-radius': 6,
            'circle-color': pos.color,
          },
        });
      }
    } catch (error) {
      // Ignoré.
    }
  }

  public handleMapStyleChange() {
    const map = this.mapStateService.map();
    if (!map) return;

    const animationState = this.animationStateService.animationState();
    const currentIndex = this.animationStateService.currentCounter;

    setTimeout(() => {
      if (animationState === 'playing' || animationState === 'paused') {
        try {
          if (map.getLayer(this.pointLayerId)) {
            map.removeLayer(this.pointLayerId);
          }
          if (map.getSource(this.pointSourceId)) {
            map.removeSource(this.pointSourceId);
          }

          this.recreateAnimationPoint(map, currentIndex > 0 ? currentIndex - 1 : 0);

          if (animationState === 'playing') {
            this.animatePoints();
          }
        } catch (error) {
          // Ignoré.
        }
      }
    }, 200);
  }
}
